import { Fragment, useEffect, useState } from 'react';
import './App.css';
import AdSlot from './AdSlot';
import PrivacyPolicy from './PrivacyPolicy';
import CookieBanner from './CookieBanner';

function getRouteFromHash() {
  return window.location.hash === '#/privacy' ? 'privacy' : 'home';
}

function App() {
  const [route, setRoute] = useState(getRouteFromHash());

  useEffect(() => {
    const onHash = () => setRoute(getRouteFromHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const navigate = (target) => {
    window.location.hash = target === 'privacy' ? '#/privacy' : '#/';
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const tools = [
    {
      name: 'ChatGPT',
      category: 'Conversational AI',
      description: 'Powerful language model for conversations, writing, and problem-solving.',
      icon: '💬',
    },
    {
      name: 'Midjourney',
      category: 'Image Generation',
      description: 'Create stunning, artistic images from simple text prompts.',
      icon: '🎨',
    },
    {
      name: 'GitHub Copilot',
      category: 'Code Assistant',
      description: 'AI pair programmer that helps you write code faster and smarter.',
      icon: '💻',
    },
    {
      name: 'ElevenLabs',
      category: 'Voice AI',
      description: 'Realistic text-to-speech and voice cloning for creators.',
      icon: '🎙️',
    },
    {
      name: 'Runway',
      category: 'Video AI',
      description: 'Generate and edit videos with cutting-edge AI models.',
      icon: '🎬',
    },
    {
      name: 'Perplexity',
      category: 'AI Search',
      description: 'Conversational search engine with cited, accurate answers.',
      icon: '🔎',
    },
  ];

  if (route === 'privacy') {
    return (
      <div className="App">
        <PrivacyPolicy onBack={() => navigate('home')} />
        <footer className="footer">
          <p>© {new Date().getFullYear()} AiToolsJournal — Curated AI for curious minds.</p>
        </footer>
      </div>
    );
  }

  return (
    <div className="App">
      <header className="hero">
        <nav className="nav">
          <div className="brand">
            <span className="brand-mark">◆</span>
            <span className="brand-name">
              Ai<span className="brand-accent">Tools</span>Journal
            </span>
          </div>
          <ul className="nav-links">
            <li><a href="#tools">Tools</a></li>
            <li><a href="#categories">Categories</a></li>
            <li><a href="#about">About</a></li>
            <li><a href="#newsletter" className="nav-cta">Subscribe</a></li>
          </ul>
        </nav>

        <div className="hero-content">
          <span className="hero-badge">✨ Your daily dose of AI</span>
          <h1 className="hero-title">
            Discover the Best <span className="gradient-text">AI Tools</span><br />
            Shaping Tomorrow
          </h1>
          <p className="hero-subtitle">
            AiToolsJournal is your trusted guide to the ever-evolving world of artificial
            intelligence. We review, compare, and curate the most powerful AI tools so you
            can build, create, and work smarter.
          </p>
          <div className="hero-actions">
            <a href="#tools" className="btn btn-primary">Explore Tools</a>
            <a href="#newsletter" className="btn btn-ghost">Join Newsletter →</a>
          </div>
          <div className="hero-stats">
            <div className="stat">
              <span className="stat-number">500+</span>
              <span className="stat-label">AI Tools Reviewed</span>
            </div>
            <div className="stat">
              <span className="stat-number">50K+</span>
              <span className="stat-label">Monthly Readers</span>
            </div>
            <div className="stat">
              <span className="stat-number">Daily</span>
              <span className="stat-label">Fresh Updates</span>
            </div>
          </div>
        </div>

        <div className="hero-orb hero-orb-1"></div>
        <div className="hero-orb hero-orb-2"></div>
        <div className="hero-grid"></div>
      </header>

      <main>
        <section className="ad-band">
          <AdSlot slot="" format="horizontal" />
        </section>

        <section id="tools" className="section">
          <div className="section-header">
            <span className="eyebrow">Featured Picks</span>
            <h2>Trending AI Tools This Week</h2>
            <p>Hand-picked tools our editors are loving right now.</p>
          </div>
          <div className="tools-grid">
            {tools.map((tool, idx) => (
              <Fragment key={tool.name}>
                <article className="tool-card">
                  <div className="tool-icon">{tool.icon}</div>
                  <span className="tool-category">{tool.category}</span>
                  <h3 className="tool-name">{tool.name}</h3>
                  <p className="tool-description">{tool.description}</p>
                  <a href="#read-more" className="tool-link">Read review →</a>
                </article>
                {idx === 2 && (
                  <div className="tool-card tool-card-ad">
                    <AdSlot slot="" format="rectangle" />
                  </div>
                )}
              </Fragment>
            ))}
          </div>
        </section>

        <section id="about" className="section about">
          <div className="about-grid">
            <div>
              <span className="eyebrow">Why AiToolsJournal</span>
              <h2>Independent reviews you can trust.</h2>
              <p>
                We test every tool ourselves — no sponsored fluff, no AI-generated filler.
                Our team of writers, engineers, and designers digs into each product so you
                know exactly what's worth your time and money.
              </p>
              <ul className="checklist">
                <li>✅ Hands-on, unbiased reviews</li>
                <li>✅ Side-by-side tool comparisons</li>
                <li>✅ Tutorials and prompt libraries</li>
                <li>✅ Weekly newsletter with the latest launches</li>
              </ul>
            </div>
            <div className="about-card">
              <h3>Stay ahead of the curve</h3>
              <p>Get the best new AI tools delivered to your inbox every Friday.</p>
              <form className="newsletter" id="newsletter" onSubmit={(e) => e.preventDefault()}>
                <input type="email" placeholder="you@example.com" aria-label="Email" />
                <button type="submit">Subscribe</button>
              </form>
              <small>No spam. Unsubscribe anytime.</small>
            </div>
          </div>
        </section>
      </main>

      <footer className="footer">
        <p>© {new Date().getFullYear()} AiToolsJournal — Curated AI for curious minds.</p>
        <p className="footer-links">
          <button className="link-btn" onClick={() => navigate('privacy')}>Privacy Policy</button>
          <span className="dot">·</span>
          <a href="mailto:hello@aitoolsjournal.example">Contact</a>
        </p>
      </footer>

      <CookieBanner onNavigate={navigate} />
    </div>
  );
}

export default App;
